import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ActivityIcon } from '../../assets/icons/activety-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calender.svg';
import { ReactComponent as CarIcon } from '../../assets/icons/car.svg';
import { ReactComponent as GraphIcon } from '../../assets/icons/chart-icon.svg';
import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon.svg';
import { ReactComponent as LightningIcon } from '../../assets/icons/flash-icon.svg';
import { ReactComponent as HistoryIcon } from '../../assets/icons/history-icon.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/user-icon.svg';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import emspSessionApi from '../../utils/api/v2/emsp-session';
import TabToggle from '../tab-toggle/TabToggle';

interface EmspSessionSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

/**
 * Formats ISO date string to "DD MMM YYYY, HH:MM AM/PM" format
 * @param {string | undefined} dateString - The ISO date string
 * @returns {string} The formatted date string
 */
const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return '—';
  try {
    const dateValue = ConvertUTCtoDate(dateString);
    const timeValue = FormatUtcString(ConvertUTCtoHHMM(dateString));
    if (dateValue && timeValue) {
      return `${dateValue}, ${timeValue}`;
    }
  } catch (error) {
    console.error(error);
  }
  return dateString;
};

/**
 * EmspSessionSidePanel component to display detail card information for an eMSP Session.
 * @param {EmspSessionSidePanelProperties} properties - Component properties.
 * @returns {React.JSX.Element} The rendered side panel.
 */
function EmspSessionSidePanel(properties: EmspSessionSidePanelProperties) {
  const { t } = useTranslation('sidePanel');
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [sessionData, setSessionData] = useState<any>();
  const [activeTab, setActiveTab] = useState<'session' | 'user'>('session');

  /**
   * Helper function to get nested values from sessionData using dot notation
   * @param {string} path - dot-separated key path
   * @returns {any} value or fallback string
   */
  const getNestedValue = (path: string): any => {
    let nestedValue: any = sessionData;
    try {
      const parts = path.split('.');
      parts.forEach((part) => {
        if (nestedValue !== null && nestedValue !== undefined) {
          const snakePart = part.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
          const resolvedKey = [part, snakePart].find(
            (key) => nestedValue[key] !== undefined && nestedValue[key] !== null,
          );
          nestedValue = resolvedKey !== undefined ? nestedValue[resolvedKey] : undefined;
        }
      });
    } catch (error) {
      console.error(error);
    }
    return nestedValue !== undefined && nestedValue !== null ? nestedValue : '—';
  };

  /**
   * Function to fetch EMSP session details
   * @param {string} sessionId - The session ID
   * @returns {Promise<void>} Resolves when fetch completes
   */
  const handleFetchSessionDetails = async (sessionId: string) => {
    emspSessionApi
      .fetchCommercialSessionById(sessionId)
      .then((response) => {
        setSessionData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message:
              error?.response?.data?.message ||
              t('errorMessage', { defaultValue: 'Failed to fetch Session details.' }),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  useEffect(() => {
    let sessionId;
    if (fetchIdFrom === 'queryParams') {
      sessionId = searchParameters.get('sessionId') || searchParameters.get('id');
    } else if (fetchIdFrom === 'props') {
      sessionId = id;
    }

    if (!sessionId) {
      return;
    }

    handleFetchSessionDetails(sessionId);
  }, [searchParameters, id]);

  const statusValue = getNestedValue('status');

  /**
   * Calculates the human-readable formatted duration text from durationSeconds or totalTime.
   * @returns {string} The formatted duration string.
   */
  const getDurationText = (): string => {
    let totalSeconds = 0;
    if (sessionData?.durationSeconds !== undefined && sessionData?.durationSeconds !== null) {
      totalSeconds = Number(sessionData.durationSeconds);
    } else if (sessionData?.totalTime !== undefined && sessionData?.totalTime !== null) {
      totalSeconds = Math.round(Number(sessionData.totalTime) * 3600);
    } else if (sessionData?.startTime && sessionData?.endTime) {
      const diffMs =
        new Date(sessionData.endTime).getTime() - new Date(sessionData.startTime).getTime();
      if (diffMs > 0) {
        totalSeconds = Math.floor(diffMs / 1000);
      }
    }

    if (totalSeconds <= 0) return '—';

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts = [
      days ? `${days} D` : '',
      hours ? `${hours} H` : '',
      minutes ? `${minutes} M` : '',
      seconds ? `${seconds} S` : '',
    ].filter(Boolean);

    return parts.join(' ') || '—';
  };
  const durationText = getDurationText();

  const energyValue = sessionData?.totalEnergy ?? sessionData?.energyKwh;
  const formattedEnergy =
    energyValue !== undefined && energyValue !== null
      ? `${ToFixedValue(Number(energyValue))} kWh`
      : '—';

  const currencyValue = getNestedValue('currency') || 'SAR';

  let costValue: any = sessionData?.totalCost;
  if (costValue && typeof costValue === 'object') {
    costValue = costValue.incl_vat ?? costValue.excl_vat;
  }
  // const formattedCost = costValue !== undefined && costValue !== null
  //   ? `${ToFixedValue(Number(costValue))} ${currencyValue}`
  //   : '—';

  const sessionInfoList = [
    {
      label: t('sessionId', { defaultValue: 'Session ID' }),
      value:
        getNestedValue('sessionId') !== '—' ? getNestedValue('sessionId') : getNestedValue('id'),
    },
    { label: t('evseUid', { defaultValue: 'EVSE UID' }), value: getNestedValue('evseUid') },
    {
      label: t('connectorId', { defaultValue: 'Connector ID' }),
      value:
        getNestedValue('connector') !== '—'
          ? getNestedValue('connector')
          : getNestedValue('connectorId'),
    },
    {
      label: t('locationId', { defaultValue: 'Location ID' }),
      value:
        getNestedValue('location.id') !== '—'
          ? getNestedValue('location.id')
          : getNestedValue('locationId'),
    },
    { label: t('partyId', { defaultValue: 'Party ID' }), value: getNestedValue('partyId') },
    {
      label: t('status', { defaultValue: 'Status' }),
      value: statusValue,
      isBadge: true,
    },
    {
      label: t('startTime', { defaultValue: 'Start Time' }),
      value: formatDateTime(sessionData?.startTime || sessionData?.sessionStartAt),
    },
    {
      label: t('endTime', { defaultValue: 'End Time' }),
      value: formatDateTime(sessionData?.endTime || sessionData?.sessionEndAt),
    },
    {
      label: t('stopSource', { defaultValue: 'Stop Source' }),
      value: getNestedValue('stopSource'),
    },
    {
      label: t('stoppedByOperatorId', { defaultValue: 'Stopped By Operator ID' }),
      value: getNestedValue('stoppedByOperatorId'),
    },
  ];

  const rawUserName = getNestedValue('userName');
  let displayUserName = '—';
  if (rawUserName !== '—') {
    displayUserName = rawUserName;
  } else {
    const firstName = getNestedValue('driver.firstName');
    const lastName = getNestedValue('driver.lastName');
    if (firstName !== '—') {
      displayUserName = `${firstName} ${lastName !== '—' ? lastName : ''}`.trim();
    }
  }

  /**
   * Helper function to extract raw phone string without nested ternaries.
   * @returns {string} Raw phone number.
   */
  const getRawPhone = (): string => {
    const driverPhone = getNestedValue('driver.phoneNumber');
    if (driverPhone !== '—') return driverPhone;
    const driverPhoneAlt = getNestedValue('driver.phone');
    if (driverPhoneAlt !== '—') return driverPhoneAlt;
    const rootPhone = getNestedValue('phoneNumber');
    if (rootPhone !== '—') return rootPhone;
    return getNestedValue('phone');
  };

  const rawPhone = getRawPhone();

  const rawCountryCode =
    getNestedValue('driver.countryCode') !== '—'
      ? getNestedValue('driver.countryCode')
      : getNestedValue('countryCode');

  let formattedPhone = '—';
  if (rawPhone !== '—' && rawPhone) {
    let cleanPhone = String(rawPhone).trim();
    if (rawCountryCode !== '—' && rawCountryCode) {
      const codeString = String(rawCountryCode).trim().replace(/^\+/, '');
      const codeWithPlus = `+${codeString}`;

      if (cleanPhone.startsWith(codeString)) {
        cleanPhone = cleanPhone.slice(codeString.length).trim();
      } else if (cleanPhone.startsWith(`+${codeString}`)) {
        cleanPhone = cleanPhone.slice(codeString.length + 1).trim();
      }

      formattedPhone = `${codeWithPlus} ${cleanPhone}`;
    } else {
      formattedPhone = cleanPhone;
    }
  }

  const driverInfoList = [
    {
      label: t('userCustomId', { defaultValue: 'User Custom ID' }),
      value:
        getNestedValue('driver.userCustomId') !== '—'
          ? getNestedValue('driver.userCustomId')
          : getNestedValue('userCustomId'),
    },
    { label: t('userName', { defaultValue: 'User Name' }), value: displayUserName },
    {
      label: t('email', { defaultValue: 'Email' }),
      value:
        getNestedValue('driver.email') !== '—'
          ? getNestedValue('driver.email')
          : getNestedValue('email'),
    },
    { label: t('phoneNumber', { defaultValue: 'Phone Number' }), value: formattedPhone },
  ];

  const vehicleInfoList = [
    {
      label: t('manufacturer', { defaultValue: 'Manufacturer' }),
      value: getNestedValue('vehicle.manufacturer'),
    },
    { label: t('model', { defaultValue: 'Model' }), value: getNestedValue('vehicle.model') },
    {
      label: t('manufacturerYear', { defaultValue: 'Manufacturer Year' }),
      value: getNestedValue('vehicle.manufacturerYear'),
    },
    {
      label: t('batteryCapacity', { defaultValue: 'Battery Capacity' }),
      value:
        getNestedValue('vehicle.batteryCapacity') !== '—'
          ? `${getNestedValue('vehicle.batteryCapacity')} kWh`
          : '—',
    },
    {
      label: t('connectorType', { defaultValue: 'Connector Type' }),
      value: getNestedValue('vehicle.connectorType'),
    },
  ];

  const summaryList = [
    { label: t('energyKwh', { defaultValue: 'Energy (kWh)' }), value: formattedEnergy },
    {
      label: t('preferredPaymentMethod', { defaultValue: 'Preferred Payment Method' }),
      value:
        getNestedValue('preferredPaymentMethod') !== '—'
          ? getNestedValue('preferredPaymentMethod')
          : getNestedValue('paymentMethod'),
    },
    // { label: t('totalCost', { defaultValue: 'Total Cost' }), value: formattedCost },
    // { label: t('totalCostCurrency', { defaultValue: `Total Cost (${currencyValue})` }), value: formattedCost },
  ];

  const timestampList = [
    {
      label: t('createdAt', { defaultValue: 'Created At' }),
      value: formatDateTime(sessionData?.createdAt),
    },
    {
      label: t('lastUpdated', { defaultValue: 'Last Updated' }),
      value: formatDateTime(sessionData?.updatedAt || sessionData?.lastUpdated),
    },
  ];

  const sessionSections = [
    {
      header: t('sessionInformation', { defaultValue: 'Session Information' }),
      icon: <CalendarIcon />,
      items: sessionInfoList,
      classKey: 'session-info',
      tab: 'session',
    },
    {
      header: t('driverInformation', { defaultValue: 'Driver Information' }),
      icon: <UserIcon />,
      items: driverInfoList,
      classKey: 'driver-info',
      tab: 'user',
    },
    {
      header: t('vehicleInformation', { defaultValue: 'Vehicle Information' }),
      icon: <CarIcon />,
      items: vehicleInfoList,
      classKey: 'vehicle-info',
      tab: 'user',
    },
    {
      header: t('sessionSummary', { defaultValue: 'Session Summary' }),
      icon: <GraphIcon />,
      items: summaryList,
      classKey: 'session-summary',
      tab: 'session',
    },
    {
      header: t('timestamps', { defaultValue: 'Timestamps' }),
      icon: <ClockIcon />,
      items: timestampList,
      classKey: 'timestamps',
      tab: 'session',
    },
  ];

  const activeSections = sessionSections.filter((section) => section.tab === activeTab);

  return (
    <div className="emsp-session-side-panel-wrap">
      {/* Scrollable Middle Content */}
      <div className="panel-scrollable-content">
        {/* 4-Metric summary row */}
        <div className="metrics-summary-row">
          {/* Card 1: Status */}
          <div className="metric-card">
            <div
              className={`badge-icon-wrap status status-${String(statusValue).toLowerCase().replace(/\s+/g, '-')}`}>
              {['finished', 'completed'].includes(String(statusValue).toLowerCase()) ? (
                <HistoryIcon />
              ) : (
                <ActivityIcon />
              )}
            </div>
            <div className="card-info">
              <span className="card-title">{t('status', { defaultValue: 'STATUS' })}</span>
              <span
                className={`card-value status-${String(statusValue).toLowerCase().replace(/\s+/g, '-')}`}>
                {statusValue.charAt(0).toUpperCase() + statusValue.slice(1)}
              </span>
              {String(statusValue).toLowerCase() === 'in progress' && (
                <span className="active-session-badge">Active Session</span>
              )}
            </div>
          </div>

          {/* Card 2: Duration */}
          <div className="metric-card">
            <div className="badge-icon-wrap duration">
              <ClockIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('durationLabel', { defaultValue: 'DURATION' })}</span>
              <span className="card-value">{durationText === '—' ? '—' : durationText}</span>
              <span className="card-subtext">
                {durationText === '—' ? 'Not Available' : 'Total Time'}
              </span>
            </div>
          </div>

          {/* Card 3: Energy */}
          <div className="metric-card">
            <div className="badge-icon-wrap energy">
              <LightningIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('energyLabel', { defaultValue: 'ENERGY' })}</span>
              <span className="card-value">{formattedEnergy}</span>
              <span className="card-subtext">
                {t('totalEnergy', { defaultValue: 'Total Energy' })}
              </span>
            </div>
          </div>

          {/* Card 4: Currency */}
          <div className="metric-card">
            <div className="card-info">
              <span className="card-title">{t('currencyLabel', { defaultValue: 'CURRENCY' })}</span>
              <span className="card-value">{currencyValue}</span>
              <span className="card-subtext">
                {t('sessionCurrency', { defaultValue: 'Session Currency' })}
              </span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '5px' }}>
          <TabToggle
            tabs={[
              {
                label: t('sessionDetails', { defaultValue: 'Session Details' }),
                value: 'session',
              },
              {
                label: t('userDetails', { defaultValue: 'User Details' }),
                value: 'user',
              },
            ]}
            defaultTab="session"
            isLabelValue
            onChange={(value) => setActiveTab(value as 'session' | 'user')}
          />
        </div>

        {/* Details Sections */}
        {activeSections.map((section, index) => (
          <div className="detail-section-card" key={index}>
            <div className={`card-header ${section.classKey}`}>
              {section.icon}
              <h3>{section.header}</h3>
            </div>
            <div className="card-body-grid">
              {section.items.map((item: any, itemIndex: number) => (
                <div className="grid-item" key={itemIndex}>
                  <span className="item-label">{item.label}</span>
                  {item.isBadge ? (
                    <span
                      className={`status-pill ${String(item.value).toLowerCase().replace(/\s+/g, '-')}`}>
                      {item.value}
                    </span>
                  ) : (
                    <span className="item-value">{item.value}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default EmspSessionSidePanel;
